import { useEffect, useState, useContext, useCallback, useMemo, createContext } from 'react'
import {
    ResourceType,
    ApiDocModuleContextType,
    BasicProviderProps,
    ResourceVersionArtifactType,
    ResourceVersionType,
    CatalogType,
    InputFieldOptions
} from '../types';
import useDataClient from '../hooks/useDataClient';
import { AppContext } from './AppContext';
import { resourcesAdapter } from '../adapter';
import ErrorBoundary from '../components/error/ErrorBoundary';
import { DashboardContext } from './DashboardContext';
import { DocumentationContext } from './DocumentationContext';
import { URLAPP } from '../config';

export const ApiDocModuleContext = createContext<ApiDocModuleContextType | null>(null);

function ApiDocModuleProvider({ children }: BasicProviderProps) {
    const appContext = useContext(AppContext)
    const dashboardContext = useContext(DashboardContext)
    const documentationContext = useContext(DocumentationContext)
    const editResourceClient = useDataClient<number>({ appContext })
    const updateVersionClient = useDataClient<number>({ appContext })
    const deleteResourceClient = useDataClient<number>({ appContext })
    const deleteArtifactClient = useDataClient<number>({ appContext })
    const linkResourcesClient = useDataClient<string>({ appContext })
    const [resourcesData, setResourcesData] = useState<ResourceType>()
    const [showForm, setShowForm] = useState(false)

    const rolesData = useMemo<InputFieldOptions[]>(() => {
        let rolesAvaibles = (dashboardContext?.appData.roles || []).filter(r => r.menu.find(m => m.path === URLAPP.apidoc) && r.id !== dashboardContext?.appData.rol)
        return rolesAvaibles.map(r => ({ label: r.name, value: r.menu.find(m => m.path === URLAPP.apidoc)?.id || 0 }))
    }, [dashboardContext?.appData])

    const availableArtifactOptions = useMemo<CatalogType[]>(() => {
        let currentArtifacts = resourcesData?.versions[0]?.artifacts.map(a => a.type) || []
        return (dashboardContext?.getCatalog('ART') || []).filter(c => c.active && !currentArtifacts.includes(String(c.value)))
    }, [dashboardContext, resourcesData])

    const reloadResources = useCallback((message?: string) => {
        dashboardContext?.updateAppData('rest')
        setShowForm(false)
        message && appContext?.setAppDialog({ message: message, type: 'success' })
    }, [])

    function linkResources(request: ResourceType[]) {
        linkResourcesClient.executeRequest(resourcesAdapter.linkResources(request))
    }

    function updateArtifacts(version: ResourceVersionType, files: File[], idResource?: number) {
        updateVersionClient.executeRequest(resourcesAdapter.updateVersion(version, files, idResource))
    }

    function confirmResourceDelete() {
        appContext?.setAppDialog({
            title: 'Aviso',
            message: `¿Desea eliminar el servicio ${resourcesData?.name}? <br/> Esto afectará la visualización de todos los usuarios con acceso a este recurso`,
            type: 'warning',
            onAccept: () => {
                if (resourcesData) {
                    deleteResourceClient.executeRequest(resourcesAdapter.deleteResource(resourcesData))
                }
            }
        })
    }

    function confirmArtifactDelete(data: ResourceVersionArtifactType) {
        appContext?.setAppDialog({
            title: 'Aviso',
            message: `¿Desea suprimir el artefacto ${data.name}?`,
            type: 'warning',
            onAccept: () => deleteArtifactClient.executeRequest(resourcesAdapter.deleteArtifact(data))
        })
    }

    const contextData = useMemo<ApiDocModuleContextType>(() => ({
        resourcesData,
        setResourcesData,
        rolesData,
        showForm,
        setShowForm,
        linkResources,
        updateArtifacts,
        confirmArtifactDelete,
        confirmResourceDelete,
        availableArtifactOptions,
        reloadResources
    }), [resourcesData, showForm, availableArtifactOptions])

    useEffect(() => {
        let requestAddedId = editResourceClient.status.data
        if (requestAddedId) {
            reloadResources(`Referencia actualizada`)
        }
    }, [editResourceClient.status.data])

    useEffect(() => {
        let response = linkResourcesClient.status.data
        if (response) {
            reloadResources(response)
        }
    }, [linkResourcesClient.status.data])

    useEffect(() => {
        let requestUpdate = updateVersionClient.status.data
        if (requestUpdate) {
            reloadResources(`Artefactos actualizados`)
        }
    }, [updateVersionClient.status.data])

    useEffect(() => {
        if (deleteArtifactClient.status.data) {
            reloadResources('Artefacto eliminado')
        }
    }, [deleteArtifactClient.status.data])

    useEffect(() => {
        if (deleteResourceClient.status.data) {
            reloadResources('Artefactos eliminados')
        }
    }, [deleteResourceClient.status.data])

    useEffect(() => {
        if ((documentationContext?.reloadExternalContent) && !dashboardContext?.offline) {
            dashboardContext?.appData.rest ? dashboardContext?.updateAppData('rest') : dashboardContext?.updateAppData('linkedResources')
        }
    }, [appContext?.user.data, documentationContext?.reloadExternalContent])

    return <ErrorBoundary>
        <ApiDocModuleContext.Provider value={contextData}>
            {children}
        </ApiDocModuleContext.Provider>
    </ErrorBoundary>
};


export default ApiDocModuleProvider